import React, { useState, useContext, createContext } from "react";
import type { FC, ReactNode } from "react";

import { RelayEnvironmentProvider } from "react-relay";
import type { IEnvironment } from "relay-runtime";

import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";

interface Context {
  // current relay environment
  environment: IEnvironment;
  // discard the current relay environment and create a new one
  resetRelayEnvironment: () => void;
}

interface Props {
  children?: ReactNode;
  // factory function for generating Relay environments
  createRelayEnvironment: (variables: { clientSideLogout: () => void }) => IEnvironment;
}

const ResettableRelayContext = createContext<Context | null>(null);

export const RelayProvider: FC<Props> = ({ children, createRelayEnvironment }) => {
  const { clientSideLogout } = useAuth();

  const [environment, setEnvironment] = useState(createRelayEnvironment({ clientSideLogout }));

  const resetRelayEnvironment = () => {
    setEnvironment(createRelayEnvironment({ clientSideLogout }));
  };

  return (
    <ResettableRelayContext.Provider value={{ environment, resetRelayEnvironment }}>
      <RelayEnvironmentProvider environment={environment}>{children}</RelayEnvironmentProvider>
    </ResettableRelayContext.Provider>
  );
};

export const useResettableRelay = (): Context => {
  const context = useContext(ResettableRelayContext);

  if (!context) {
    throw new Error("useResettableRelay must be used within an RelayProvider");
  }

  return context;
};
